import { useClient } from "./ClientContext";
import { getOnStorage, setOnStorage } from "@/shared";
import { DiscountProps, DiscountAtrProps, DiscountStorageProps } from "@/types";
import { HandlePrices } from "@/shared/calculatePrices";

import {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

interface HookProps {
  eraseDiscounts: () => void;
  clearClientDiscount: (id?: number) => void;
  discounts: { [key: number]: DiscountProps };
  setDiscount: (item: DiscountAtrProps & { saldoAFaturar?: number }) => void;
  deleteDiscount: (item: DiscountAtrProps) => void;
  getDiscountProduct: (productId: DiscountAtrProps) => DiscountStorageProps & { saldoAFaturar?: number };
}

const DiscountFlexContext = createContext<HookProps>({} as HookProps);

// Desempate para updatedAt (Date.now()) quando duas porções irmãs gravam no mesmo milissegundo —
// só existe um DiscountFlexProvider montado no app, então um contador de módulo é seguro aqui.
let discountWriteSeq = 0;

const DiscountFlexProvider: React.FC<any> = ({ children }) => {
  const isLoaded = useRef(false);
  const { selectedClient } = useClient();

  // Com uid, a chave é por linha do carrinho — sem isso, duas linhas do mesmo produto compartilhariam flex.
  const mountKey = (i: DiscountAtrProps) =>
    i.uid ? `${i.productId}-${i.package}-${i.quantity}-${i.uid}` : `${i.productId}-${i.package}-${i.quantity}`;

  const [discounts, setHookDiscount] = useState(
    {} as { [key: number]: DiscountProps }
  );

  const getDiscountProduct = useCallback(
    (i: DiscountAtrProps) => {
      return discounts?.[selectedClient.COD_CLIENTE]?.[mountKey(i)];
    },
    [discounts, selectedClient]
  );

  const clearClientDiscount = useCallback(
    (id?: number) => {
      setHookDiscount((prevDiscounts) => {
        const discountBase = { ...prevDiscounts };
        delete discountBase?.[id || selectedClient.COD_CLIENTE];
        const allDiscounts = { ...(discountBase || {}) };

        setOnStorage("discounts", allDiscounts);
        return allDiscounts;
      });
    },
    [selectedClient.COD_CLIENTE]
  );

  const eraseDiscounts = useCallback(() => {
    const allDiscounts = {};

    setHookDiscount(allDiscounts);
    setOnStorage("discounts", allDiscounts);
  }, []);

  const deleteDiscount = useCallback(
    (i: DiscountAtrProps) => {
      setHookDiscount((prevDiscounts) => {
        const newDiscounts = { ...(prevDiscounts?.[selectedClient.COD_CLIENTE] || {}) };

        delete newDiscounts[mountKey(i)];

        const allDiscounts = { ...(prevDiscounts || {}), [selectedClient.COD_CLIENTE]: newDiscounts };

        setOnStorage("discounts", allDiscounts);
        return allDiscounts;
      });
    },
    [selectedClient.COD_CLIENTE]
  );

  // setDiscount SEMPRE deriva o próximo estado a partir do valor anterior (prevDiscounts), nunca do
  // `discounts` capturado no closure do callback — senão duas porções irmãs do mesmo produto+combo
  // (que podem chamar setDiscount na mesma leva de effects, ex.: sincronização de flex em
  // cartProductCard) leem o mesmo snapshot antigo e a segunda chamada sobrescreve/perde a primeira.
  const setDiscount = useCallback(
    (i: DiscountAtrProps & { saldoAFaturar?: number }) => {
      const key = mountKey(i);

      setHookDiscount((prevDiscounts) => {
        const currentClientDiscounts = prevDiscounts?.[selectedClient.COD_CLIENTE] || {};
        const existing = currentClientDiscounts[key];
        const incomingPrices = (i.price || {}) as Record<string, number>;
        const hasIncomingPrices = Object.keys(incomingPrices).some(
          (cond) => Number(incomingPrices[cond]) > 0
        );
        const percentual = typeof i.percentual === 'number' ? i.percentual : 0;

        // Nunca sobrescrever discountPrice válido com mapa vazio (reaplicar sem alterar) — EXCETO
        // quando percentual volta a 0: aí o preço com desconto tem que ser limpo de verdade, senão
        // consumidores que leem discountPrice direto (ex: cartProductCard) continuam mostrando o
        // preço antigo mesmo sem nenhum flex ativo.
        const discountPrice = hasIncomingPrices
          ? incomingPrices
          : percentual === 0
            ? {}
            : existing?.discountPrice || {};

        const updatedClientDiscounts = {
          ...currentClientDiscounts,
          [key]: {
            discountPrice,
            percentual, // agora pode ser >0 (acréscimo) ou <0 (desconto)
            saldoAFaturar: i.saldoAFaturar || 0,
            updatedAt: Date.now(),
            updatedAtSeq: ++discountWriteSeq,
          }
        };

        const allDiscounts = {
          ...(prevDiscounts || {}),
          [selectedClient.COD_CLIENTE]: updatedClientDiscounts
        };

        setOnStorage('discounts', allDiscounts);
        return allDiscounts;
      });
    },
    [selectedClient.COD_CLIENTE]
  );

  useEffect(() => {
    if (isLoaded.current) return;

    const discounts = getOnStorage("discounts") as {
      [key: number]: DiscountProps;
    };
    isLoaded.current = true;
    setHookDiscount(discounts);
  }, []);

  return (
    <DiscountFlexContext.Provider
      value={{
        discounts,
        setDiscount,
        deleteDiscount,
        eraseDiscounts,
        getDiscountProduct,
        clearClientDiscount,
      }}
    >
      {children}
    </DiscountFlexContext.Provider>
  );
};

const useDiscountFlex = (): HookProps => {
  const ctx = useContext(DiscountFlexContext);

  if (!ctx) throw new Error("Erro ao usar hook Discount");

  return ctx;
};

export { DiscountFlexProvider, useDiscountFlex };
